import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

/**
 * Yadid Advisory branded sign-in.
 * Mirrors the letterhead used on every exported memo: full-bleed navy panel
 * anchoring the wordmark on the left, cream form panel on the right, single
 * ochre hairline dividing the two. Everything set in serif or the same
 * uppercase mono-tracked micro-caps used in the letterhead footer.
 */

// Palette lifted verbatim from services/personal_briefs.py :: _render_morning_email
const NAVY = "#0F1E3D";
const OCHRE = "#B08048";
const CREAM = "#F5F5F4";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]" style={{ background: CREAM }} data-testid="login-page">
      {/*
        Iter 61 — First-paint choreography for the letterhead.
        The Ochre corner hairlines draw in from their anchor corners,
        the top eyebrow fades in, then the wordmark blooms, and the
        bottom footer arrives last. Everything is CSS-only and runs
        once on mount (no JS state), and respects prefers-reduced-motion.
      */}
      <style>{`
        @keyframes yadid-fade-in {
          0% { opacity: 0; transform: translateY(6px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes yadid-logo-in {
          0% { opacity: 0; transform: scale(0.985); filter: blur(2px); }
          60% { filter: blur(0); }
          100% { opacity: 1; transform: scale(1); filter: blur(0); }
        }
        @keyframes yadid-hairline-h-lr { 0% { transform: scaleX(0); } 100% { transform: scaleX(1); } }
        @keyframes yadid-hairline-v-tb { 0% { transform: scaleY(0); } 100% { transform: scaleY(1); } }
        @keyframes yadid-hairline-h-rl { 0% { transform: scaleX(0); } 100% { transform: scaleX(1); } }
        @keyframes yadid-hairline-v-bt { 0% { transform: scaleY(0); } 100% { transform: scaleY(1); } }

        .yadid-eyebrow { opacity: 0; animation: yadid-fade-in 700ms cubic-bezier(0.22,0.61,0.36,1) 240ms forwards; }
        .yadid-logo   { opacity: 0; animation: yadid-logo-in 1200ms cubic-bezier(0.22,0.61,0.36,1) 480ms forwards; will-change: opacity, transform, filter; }
        .yadid-footer { opacity: 0; animation: yadid-fade-in 700ms cubic-bezier(0.22,0.61,0.36,1) 1100ms forwards; }

        .yadid-hair-tl-h { transform-origin: left center;   animation: yadid-hairline-h-lr 900ms cubic-bezier(0.65,0,0.35,1) 120ms both; }
        .yadid-hair-tl-v { transform-origin: center top;    animation: yadid-hairline-v-tb 900ms cubic-bezier(0.65,0,0.35,1) 120ms both; }
        .yadid-hair-br-h { transform-origin: right center;  animation: yadid-hairline-h-rl 900ms cubic-bezier(0.65,0,0.35,1) 220ms both; }
        .yadid-hair-br-v { transform-origin: center bottom; animation: yadid-hairline-v-bt 900ms cubic-bezier(0.65,0,0.35,1) 220ms both; }

        @media (prefers-reduced-motion: reduce) {
          .yadid-eyebrow, .yadid-logo, .yadid-footer { opacity: 1 !important; transform: none !important; filter: none !important; animation: none !important; }
          .yadid-hair-tl-h, .yadid-hair-tl-v, .yadid-hair-br-h, .yadid-hair-br-v { transform: none !important; animation: none !important; }
        }
      `}</style>
      {/* ─── Letterhead panel ────────────────────────────────────────── */}
      {/*
        Layout changed (Feb 2026) — the logo is now absolutely centered in
        the panel with equal margin on all four sides. The top eyebrow,
        Brass corner marks, and bottom footer are all `position: absolute`
        overlays so they can't offset the logo's optical centre.
      */}
      <div className="hidden lg:flex relative overflow-hidden items-center justify-center p-6 xl:p-10" style={{ background: NAVY, color: CREAM }}>
        {/* Faint noise texture — same effect as the PDF letterhead paper feel */}
        <div className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{
          backgroundImage: "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "3px 3px",
        }} />
        {/* Ochre corner hairlines — draw in from their anchor corners on first paint */}
        <div className="yadid-hair-tl-h pointer-events-none absolute top-6 left-6 w-24 h-px" style={{ background: OCHRE, opacity: 0.7 }} />
        <div className="yadid-hair-tl-v pointer-events-none absolute top-6 left-6 w-px h-24" style={{ background: OCHRE, opacity: 0.7 }} />
        <div className="yadid-hair-br-h pointer-events-none absolute bottom-6 right-6 w-24 h-px" style={{ background: OCHRE, opacity: 0.7 }} />
        <div className="yadid-hair-br-v pointer-events-none absolute bottom-6 right-6 w-px h-24" style={{ background: OCHRE, opacity: 0.7 }} />

        {/* Top eyebrow (absolute — does not affect logo centering) */}
        <div className="yadid-eyebrow absolute top-8 xl:top-12 left-8 xl:left-12 z-10 flex items-center gap-3">
          <div className="text-[0.6875rem] tracking-[0.3em] uppercase font-mono" style={{ color: OCHRE }}>
            Yadid Advisory · Est. 2026
          </div>
        </div>

        {/*
          Logo — the ONLY flex child so its margins collapse to the
          panel's own padding on every side. Feb 2026 · Iter 59b —
          ceilings pushed further so the mark and its baked-in
          "THE STRATEGIC OPERATING SYSTEM · FOR YADID ADVISORY LLC."
          subtitle read at full letterhead scale. The subtitle lives
          inside the artwork itself and only becomes legible when the
          image is rendered large; sighted users see it there, and the
          sr-only line remains for assistive tech.
        */}
        <div className="relative z-10 w-full flex items-center justify-center">
          <img
            src="/brand/yadidos-logo-navy-transparent.png"
            alt="YadidOS — The strategic operating system for the trusted advisor at Yadid Advisory LLC."
            className="yadid-logo w-full h-auto max-w-[760px] lg:max-w-[960px] xl:max-w-[1100px] 2xl:max-w-[1200px] select-none pointer-events-none"
            draggable={false}
            data-testid="login-logo"
            style={{ imageRendering: "auto" }}
          />
          {/* Screen-reader-only tagline — the visible subtitle is baked
              into the artwork. This line only exists so AT reads the
              partner-approved wording even though alt="" already has it. */}
          <p className="sr-only" data-testid="login-tagline">
            The strategic operating system for the trusted advisor at Yadid Advisory LLC.
          </p>
        </div>

        {/* Bottom footer (absolute — does not affect logo centering) */}
        <div className="yadid-footer absolute bottom-8 xl:bottom-12 left-8 xl:left-12 right-8 xl:right-12 z-10 flex items-end justify-between text-[0.6875rem] tracking-[0.3em] uppercase font-mono">
          <div style={{ color: "rgba(245,245,244,0.55)" }}>
            Confidential · firm-privileged
          </div>
          <div style={{ color: OCHRE }}>
            YadidOS
          </div>
        </div>
      </div>

      {/* ─── Form panel ─────────────────────────────────────────────── */}
      {/*
        Iter 60 — On mobile the logo used to live INSIDE `<form
        max-w-sm>`, which capped it at 384px regardless of image
        `max-w`. It also read as visually left-shifted because the
        wordmark's optical centre sits well to the right of its
        bounding box (dense emblem on the left, wide text on the
        right).

        Iter 60b — And the panel's own `p-6` (24px each side) was the
        real ceiling on 390px devices (only 342px of usable width).
        Reduced mobile side padding and broke the logo container out
        with `-mx-4` so it spans the full mobile viewport, then let
        the image scale up to a proper letterhead-on-a-phone size.
      */}
      <div className="flex flex-col items-center justify-center px-4 sm:px-12 py-8 sm:py-12" style={{ background: CREAM }}>
        {/* Mobile-only logo — full container width (breaks out of the
            panel side padding via -mx-4) so it can render as large as
            the phone allows. */}
        <div className="lg:hidden w-screen sm:w-full max-w-none sm:max-w-[560px] -mx-4 sm:mx-0 px-4 sm:px-0 text-center mb-10" data-testid="login-logo-mobile-wrap">
          <img
            src="/brand/yadidos-logo-light.png"
            alt="YadidOS — The strategic operating system for the trusted advisor at Yadid Advisory LLC."
            className="mx-auto w-full h-auto max-w-[560px] select-none pointer-events-none"
            draggable={false}
            data-testid="login-logo-mobile"
          />
          <p className="sr-only">
            The strategic operating system for the trusted advisor at Yadid Advisory LLC.
          </p>
        </div>

        <form onSubmit={onSubmit} className="w-full max-w-sm" data-testid="login-form">
          <div className="text-[0.625rem] font-mono uppercase tracking-[0.28em]" style={{ color: OCHRE }}>
            Firm access
          </div>
          <h2 className="mt-3" style={{ fontFamily: "Georgia, serif", fontSize: "2.25rem", fontWeight: 700, color: NAVY, letterSpacing: "-0.01em", lineHeight: 1.1 }}>
            Sign in.
          </h2>
          <p className="mt-2 text-sm italic" style={{ color: "#6B7280", fontFamily: "Georgia, serif" }}>
            Continue to your firm workspace.
          </p>

          <div className="mt-8 h-px" style={{ background: OCHRE, opacity: 0.6 }} />

          <div className="mt-8 space-y-5">
            <div>
              <Label className="text-[0.625rem] uppercase font-mono tracking-[0.24em]" style={{ color: NAVY, opacity: 0.7 }}>Email</Label>
              <Input
                data-testid="login-email"
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                className="rounded-none mt-2 h-11 bg-white border-0 border-b-2 focus-visible:ring-0 focus-visible:ring-offset-0 text-base"
                style={{ borderBottomColor: NAVY, color: NAVY, fontFamily: "Georgia, serif" }}
              />
            </div>
            <div>
              <Label className="text-[0.625rem] uppercase font-mono tracking-[0.24em]" style={{ color: NAVY, opacity: 0.7 }}>Password</Label>
              <Input
                data-testid="login-password"
                required
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="rounded-none mt-2 h-11 bg-white border-0 border-b-2 focus-visible:ring-0 focus-visible:ring-offset-0 text-base"
                style={{ borderBottomColor: NAVY, color: NAVY, fontFamily: "Georgia, serif" }}
              />
            </div>
          </div>

          <Button
            data-testid="login-submit"
            disabled={busy}
            className="w-full rounded-none mt-8 h-12 border-0 hover:opacity-90 transition-opacity"
            style={{ background: NAVY, color: CREAM, letterSpacing: "0.14em", textTransform: "uppercase", fontFamily: "Helvetica, Arial, sans-serif", fontSize: "0.7rem", fontWeight: 700 }}
          >
            {busy ? "Signing in…" : "Enter YadidOS"}
          </Button>

          <p className="text-xs mt-8 text-center" style={{ color: "#6B7280" }}>
            New to the firm?{" "}
            <Link to="/register" className="hover:underline" style={{ color: NAVY, fontWeight: 600 }} data-testid="link-register">
              Request access
            </Link>
          </p>

          <div className="mt-16 text-[0.5625rem] font-mono uppercase tracking-[0.28em] text-center" style={{ color: "#8A8A80" }}>
            Firm-privileged · Prepared by YadidAi
          </div>
        </form>
      </div>
    </div>
  );
}
